import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  selectCurrentUserAccountId,
  selectGetAllTeamsStatus,
  selectIsCurrentUserAdmin,
} from "app/containers/Global/slice";
import { selectGettingSingleUserManagerAndDirectReportsStatus } from "app/containers/AdvancedCsvUpload/slice";
import {
  getSubordinatesWeeklyCheckInActivityReport,
  getSubordinatesWeeklyCheckInLineChartData,
  getWeeklyCheckInModuleStatus,
  selectSubordinatesWeeklyCheckInActivityReports,
  selectSubordinatesWeeklyCheckInLineChartData,
  selectGetSubordinatesWeeklyCheckInLineChartDataStatus,
  selectUsersSubordinatesIds,
  selectGetSpanOfControlStatus,
  getUserSpanOfControl,
  getWeeklyCheckInCelebrateAndSupportForSubordinates,
  selectCelebrateAndSupports,
  selectGetWeeklyCheckInCelebrateAndSupportForSubordinatesStatus,
  selectWeeklyCheckInCompanySettings,
} from "../slice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { trackFeatureViewed } from "utils/trackingFunctions";
import WeeklyCheckInActivitySnapshot from "./WeeklyCheckInActivitySnapshot";
import SimplePopover from "app/components/Popovers/SimplePopover";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CelebrateAndSupportContainer from "../CelebrateAndSupportContainer";
import { selectTeamAndDepartmentLeadIdsForLoggedInUser } from "app/containers/AdminConsole/slice";
import IndividualContributorWeeklyCheckInSnapshot from "./IndividualContributorWeeklyCheckInSnapshot";
import { WEEKLY_CHECK_IN_FEATURE_NAME } from "../constants";

const SubordinatesWeeklyCheckinSnapshot = () => {
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(selectIsCurrentUserAdmin);
  const currentUserAccountId = useAppSelector(selectCurrentUserAccountId);
  const getTeamsStatus = useAppSelector(selectGetAllTeamsStatus);
  const getManagerAndDirectReportsStatus = useAppSelector(
    selectGettingSingleUserManagerAndDirectReportsStatus
  );
  const subordinatesWeeklyCheckInActivityReports = useAppSelector(
    selectSubordinatesWeeklyCheckInActivityReports
  );
  const subordinatesLineChartData = useAppSelector(
    selectSubordinatesWeeklyCheckInLineChartData
  );
  const fetchingWeeklyCheckInLineChartDataStatus = useAppSelector(
    selectGetSubordinatesWeeklyCheckInLineChartDataStatus
  );
  const teamsAndDepartmentsUserIsLeaderOf = useAppSelector(
    selectTeamAndDepartmentLeadIdsForLoggedInUser
  );
  const usersSubordinatesIds = useAppSelector(selectUsersSubordinatesIds);
  const getSpanOfControlStatus = useAppSelector(selectGetSpanOfControlStatus);
  const celebrateAndSupports = useAppSelector(selectCelebrateAndSupports);
  const getWeeklyCheckInCelebrateAndSupportStatus = useAppSelector(
    selectGetWeeklyCheckInCelebrateAndSupportForSubordinatesStatus
  );
  const weeklyCheckInCompanySettings = useAppSelector(
    selectWeeklyCheckInCompanySettings
  );

  const [
    weeklyCheckInActivityTimeInterval,
    setWeeklyCheckInActivityTimeInterval,
  ] = useState("Past 7 days");
  const [lineChartTimeInterval, setLineChartTimeInterval] =
    useState("Past 30 days");
  const [lineChartNavigationIndex, setLineChartNavigationIndex] = useState(1);
  const [hasFeatureBeenViewed, setHasFeatureBeenViewed] = useState(false);

  useEffect(() => {
    const cadence = weeklyCheckInCompanySettings?.checkInFrequency ?? "weekly";
    if (cadence === "monthly") {
      setWeeklyCheckInActivityTimeInterval("Past 30 days");
      setLineChartTimeInterval("Past 365 days");
    } else if (cadence === "biweekly") {
      setWeeklyCheckInActivityTimeInterval("Past 14 days");
      setLineChartTimeInterval("Past 30 days");
    } else {
      setWeeklyCheckInActivityTimeInterval("Past 7 days");
      setLineChartTimeInterval("Past 30 days");
    }
  }, [weeklyCheckInCompanySettings?.checkInFrequency]);

  const userHasSubordinates = useMemo(() => {
    const subordinateLength = usersSubordinatesIds?.length ?? 0;
    return subordinateLength > 1;
  }, [usersSubordinatesIds]);

  const isIndividualContributor = useMemo(() => {
    if (teamsAndDepartmentsUserIsLeaderOf?.teams?.length) return false;
    return !userHasSubordinates;
  }, [userHasSubordinates, teamsAndDepartmentsUserIsLeaderOf]);

  useEffect(() => {
    if (hasFeatureBeenViewed || getSpanOfControlStatus !== "succeeded") return;
    trackFeatureViewed(
      `Weekly Check-In Activity Dashboard Card (${
        userHasSubordinates ? "Direct Reports" : "Individual Contributor"
      })`
    );
    setHasFeatureBeenViewed(true);
  }, [hasFeatureBeenViewed, getSpanOfControlStatus, userHasSubordinates]);

  useEffect(() => {
    dispatch(getSubordinatesWeeklyCheckInActivityReport());
    dispatch(getUserSpanOfControl());
    dispatch(getWeeklyCheckInModuleStatus());
    dispatch(
      getSubordinatesWeeklyCheckInLineChartData({
        timeInterval: "last30Days",
        index: 1,
      })
    );
  }, [dispatch]);

  const onGetWeeklyCheckInLineChartData = useCallback(
    ({ timeInterval, index }: { timeInterval: string; index: number }) => {
      dispatch(
        getSubordinatesWeeklyCheckInLineChartData({
          timeInterval,
          index,
        })
      );
    },
    [dispatch]
  );

  const getHeaderCornerContent = () => {
    if (!userHasSubordinates) return null;
    return (
      <SimplePopover
        popoverContent={
          <p>
            These metrics reflect check-in activity for everyone in your
            reporting structure, including yourself, people who report to you,
            and teams you lead.
          </p>
        }
        trigger="hover"
        hideTitleAndCloseButton
      >
        <div className="info-hover-over-element">
          <FontAwesomeIcon
            icon={["far", "info-circle"]}
            className="info-icon"
          />
          Who's included in this?
        </div>
      </SimplePopover>
    );
  };

  const getAdditionalFooterContent = () => {
    // if user is not a department leader, team leader, and manager; return null
    if (
      !userHasSubordinates &&
      !teamsAndDepartmentsUserIsLeaderOf?.teams?.length &&
      !teamsAndDepartmentsUserIsLeaderOf?.departments?.length
    ) {
      return null;
    }

    return (
      <CelebrateAndSupportContainer
        celebrateAndSupports={celebrateAndSupports}
        onGetWeeklyCheckInCelebrateAndSupport={(payload) =>
          dispatch(getWeeklyCheckInCelebrateAndSupportForSubordinates(payload))
        }
        additionalQueryParams={{ reportsOnly: true }}
        isCelebrateAndSupportsBeingFetched={
          getWeeklyCheckInCelebrateAndSupportStatus === "loading"
        }
        hideEntityPickers
      />
    );
  };

  const getMerchandisingType = () => {
    if (isAdmin) {
      return "admin";
    }
    if (!userHasSubordinates) return "individual-contributor";
    return "manager";
  };

  const getBodyWithLoading = () => {
    if (
      getTeamsStatus === "loading" ||
      getManagerAndDirectReportsStatus === "loading" ||
      !currentUserAccountId
    ) {
      return null;
    }

    if (
      !userHasSubordinates &&
      weeklyCheckInCompanySettings?.visibilityMode !== "open"
    ) {
      return <IndividualContributorWeeklyCheckInSnapshot />;
    }

    return (
      <WeeklyCheckInActivitySnapshot
        coachBoWeeklyCheckInActivityReports={
          subordinatesWeeklyCheckInActivityReports
        }
        getCoachBoWeeklyCheckInActivityReportStatus={"succeeded"}
        showWeeklyCheckInActivityEmptyState={false}
        weeklyCheckInLineChartData={subordinatesLineChartData}
        fetchingWeeklyCheckInLineChartDataStatus={
          fetchingWeeklyCheckInLineChartDataStatus
        }
        getShowWeeklyCheckInActivityEmptyStateStatus={"succeeded"}
        viewActivityPath="/DirectReportsWeeklyCheckin"
        weeklyCheckInActivityTimeInterval={weeklyCheckInActivityTimeInterval}
        setWeeklyCheckInActivityTimeInterval={
          setWeeklyCheckInActivityTimeInterval
        }
        lineChartTimeInterval={lineChartTimeInterval}
        setLineChartTimeInterval={setLineChartTimeInterval}
        lineChartNavigationIndex={lineChartNavigationIndex}
        setLineChartNavigationIndex={setLineChartNavigationIndex}
        onGetWeeklyCheckInLineChartData={onGetWeeklyCheckInLineChartData}
        headerCornerContent={getHeaderCornerContent()}
        showingOnlySubordinates
        userHasNoSubordinates={!userHasSubordinates}
        additionalFooterContent={getAdditionalFooterContent()}
        actionButtonEmptyContent={
          isIndividualContributor ? (
            <p>
              You haven't completed any Check-Ins yet. Once you complete a{" "}
              {WEEKLY_CHECK_IN_FEATURE_NAME}, you will see your scores here.
            </p>
          ) : null
        }
        merchandisingType={getMerchandisingType()}
      />
    );
  };

  return getBodyWithLoading();
};

export default SubordinatesWeeklyCheckinSnapshot;
